import { useState, useEffect, useRef } from 'react';
import whiteStarPic from '@/assets/images/white-star.png';
import type { MostValuableCompany as CompanyType, Power20Company } from '@/types/company';
import { getMostValuableCompanies, getPower20Companies } from '@/api/company';

export default function Company() {
  const [mostValuableCompanies, setMostValuableCompanies] = useState<CompanyType[]>([]);
  const [power20Companies, setPower20Companies] = useState<Power20Company[]>([]);
  const [mostValuableCompaniesLoading, setMostValuableCompaniesLoading] = useState(true);
  const [power20CompaniesLoading, setPower20CompaniesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFirstRender = useRef(true);
  const isSecondRender = useRef(true);

  // 获取 MostValuableCompanies
  useEffect(() => {
    const fetchMostValuableCompanies = async () => {
      if (!isFirstRender.current) return;
      isFirstRender.current = false;

      try {
        setMostValuableCompaniesLoading(true);
        const response = await getMostValuableCompanies('',1,7,'valuation','DESC',[]);
        if (response && 'content' in response && Array.isArray(response.content)) {
          setMostValuableCompanies(response.content);
        } else {
          setError('返回数据格式错误');
          setMostValuableCompanies([]);
        }
      } catch (err) {
        console.error('Error fetching most valuable companies:', err);
        setMostValuableCompanies([]);
      } finally {
        setMostValuableCompaniesLoading(false);
      }
    };

    fetchMostValuableCompanies();
  }, []);

  // 获取 Power20Companies
  useEffect(() => {
    const fetchPower20Companies = async () => {
      if (!isSecondRender.current) return;
      isSecondRender.current = false;

      try {
        setPower20CompaniesLoading(true);
        const response = await getPower20Companies();
        if (Array.isArray(response)) {
          setPower20Companies(response);
        } else {
          console.error('Power20 companies data format error');
          setPower20Companies([]);
        }
      } catch (err) {
        console.error('Error fetching power 20 companies:', err);
        setPower20Companies([]);
      } finally {
        setPower20CompaniesLoading(false);
      }
    };

    fetchPower20Companies();
  }, []);

  return (
    <div className="py-16">
      <div className="flex items-center gap-3 mb-12">
        <img src={whiteStarPic} alt="Star" className="h-[24px] sm:h-[28px] md:h-[32px] lg:h-[36px] w-auto" />
        <h2 className="text-3xl font-bold">关于我们</h2>
      </div>
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-900 p-8 rounded-xl mb-8">
          <h3 className="text-2xl font-bold mb-4">公司简介</h3>
          <p className="text-gray-300 leading-relaxed">
            Polaris Invest 是一家专注于全球科技创新的投资平台。我们致力于为投资者提供优质的投资机会，
            特别是在人工智能、太空科技、生物科技等前沿领域。通过专业的投资团队和严格的风控体系，
            我们为投资者创造长期价值。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-gray-900 p-6 rounded-xl">
            <h3 className="text-xl font-bold mb-4">我们的使命</h3>
            <p className="text-gray-300">
              通过投资推动科技创新，为全球投资者创造价值，同时促进科技进步和社会发展。
            </p>
          </div>

          <div className="bg-gray-900 p-6 rounded-xl">
            <h3 className="text-xl font-bold mb-4">我们的愿景</h3>
            <p className="text-gray-300">
              成为全球领先的科技创新投资平台，连接投资者与最具创新力的科技企业。
            </p>
          </div>
        </div>

        {/* Power20 Companies Section */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">Power 20 公司</h3>
          {power20CompaniesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            </div>
          ) : error ? (
            <div className="bg-red-900/50 text-red-200 p-4 rounded-xl">
              {error}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {power20Companies.map((company) => (
                <div key={company.id} className="bg-gray-900 p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <img
                        src={company.logoLink}
                        alt={company.name}
                        className="w-16 h-16 object-contain"
                        style={{ backgroundColor: company.logoColor }}
                      />
                      <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                        {company.rank}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-2">{company.name}</h4>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <span className={`px-2 py-1 rounded ${
                          company.priceDeltaPercentage >= 0 
                            ? 'bg-green-900/50 text-green-200' 
                            : 'bg-red-900/50 text-red-200'
                        }`}>
                          {company.priceDeltaPercentage >= 0 ? '+' : ''}{company.priceDeltaPercentage.toFixed(2)}%
                        </span>
                        <span className="bg-purple-900/50 text-purple-200 px-2 py-1 rounded">
                          {`$${company.price.toFixed(2)}`}
                        </span>
                        <span className="bg-blue-900/50 text-blue-200 px-2 py-1 rounded">
                          {`$${(company.valuation / 1000000000).toFixed(2)}B`}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-400">
                        {company.isMarketActive ? '市场活跃' : '市场未活跃'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Most Valuable Companies Section */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">投资组合</h3>
          {mostValuableCompaniesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            </div>
          ) : error ? (
            <div className="bg-red-900/50 text-red-200 p-4 rounded-xl">
              {error}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mostValuableCompanies.map((company) => (
                <div key={company.id} className="bg-gray-900 p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <img
                      src={company.logoLink}
                      alt={company.name}
                      className="w-16 h-16 object-contain"
                      style={{ backgroundColor: company.logoColor }}
                    />
                    <div>
                      <h4 className="text-xl font-bold mb-2">{company.name}</h4>
                      <p className="text-gray-400 text-sm mb-2">{company.description}</p>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <span className="bg-blue-900/50 text-blue-200 px-2 py-1 rounded">
                          {company.category}
                        </span>
                        <span className="bg-green-900/50 text-green-200 px-2 py-1 rounded">
                          {company.isMarketActive ? '市场活跃' : '市场未活跃'}
                        </span>
                        <span className="bg-purple-900/50 text-purple-200 px-2 py-1 rounded">
                          {`估值: $${(company.valuation / 1000000000).toFixed(2)}B`}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-400">
                        <span>{company.location}</span>
                        <span className="mx-2">•</span>
                        <span>成立于 {company.foundedYear}</span>
                        <span className="mx-2">•</span>
                        <span>{company.numWatching} 人关注</span>
                      </div>
                      <div className="mt-2 flex gap-4 text-sm">
                        {company.twitterHandle && (
                          <a href={`https://twitter.com/${company.twitterHandle}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                            Twitter
                          </a>
                        )}
                        {company.linkedinUrl && (
                          <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                            LinkedIn
                          </a>
                        )}
                        {company.crunchbaseUrl && (
                          <a href={company.crunchbaseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                            Crunchbase
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
